import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import i18n, { $tcf, $tf } from "@/plugins/i18n";
import cleave from "./directives/cleave";
import router from "./router";
import store from "./store";
import Buefy from "buefy";
import VueApexCharts from "vue3-apexcharts";
import PrimeVue from "primevue/config";
import Aura from "@primevue/themes/aura";
import "primeicons/primeicons.css";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import Tooltip from "primevue/tooltip";
import "./utils/stringExtensions";

import "@/assets/scss/main.scss";
import {
  hourToShortWorkDayFormat,
  initDatePrototypes,
  secondToShortWorkDayFormat,
  secondToTimeFormat,
  setConfirm,
} from "./utils/util";
import dayjs from "dayjs";
import VueTelInput from "vue3-tel-input";
import "vue3-tel-input/dist/vue3-tel-input.css";
import * as FChartPlugin from "@/plugins/fchart";
import { definePreset } from "@primevue/themes";
import VueCal from "vue-cal";

initDatePrototypes();

const ForecastifyPreset = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        surface: {
          0: "#ffffff",
          50: "{slate.50}",
          100: "{slate.100}",
          200: "{slate.200}",
          300: "{slate.300}",
          400: "{slate.400}",
          500: "{slate.500}",
          600: "{slate.600}",
          700: "{slate.700}",
          800: "{slate.800}",
          900: "{slate.900}",
          950: "{slate.950}",
        },
        primary: {
          color: "var(--p-ffyblue-500)",
          50: "var(--p-ffyblue-50)",
          100: "var(--p-ffyblue-100)",
          200: "var(--p-ffyblue-200)",
          300: "var(--p-ffyblue-300)",
          400: "var(--p-ffyblue-400)",
          500: "var(--p-ffyblue-500)",
          600: "var(--p-ffyblue-600)",
          700: "var(--p-ffyblue-700)",
          800: "var(--p-ffyblue-800)",
          900: "var(--p-ffyblue-900)",
          950: "var(--p-ffyblue-950)",
        },
      },
      dark: {
        surface: {
          0: "#000000",
          50: "{zinc.950}",
          100: "{zinc.900}",
          200: "{zinc.800}",
          300: "{zinc.700}",
          400: "{zinc.600}",
          500: "{zinc.500}",
          600: "{zinc.400}",
          700: "{zinc.300}",
          800: "{zinc.200}",
          900: "{zinc.100}",
          950: "{zinc.50}",
        },
        primary: {
          color: "var(--p-ffyblue-400)",
          50: "var(--p-ffyblue-950)",
          100: "var(--p-ffyblue-900)",
          200: "var(--p-ffyblue-800)",
          300: "var(--p-ffyblue-700)",
          400: "var(--p-ffyblue-600)",
          500: "var(--p-ffyblue-500)",
          600: "var(--p-ffyblue-400)",
          700: "var(--p-ffyblue-300)",
          800: "var(--p-ffyblue-200)",
          900: "var(--p-ffyblue-100)",
          950: "var(--p-ffyblue-50)",
        },
        formField: {
          color: "#ffffff",
          background: "{slate.950}", // "{zinc.950}",
          borderColor: "{slate.600}", // "{zinc.600}",
          placeholderColor: "{slate.400}", // "{zinc.400}",
        },
      },
    },
  },
});

import StyleClass from "primevue/styleclass";
import { defaultOptions } from "primevue";
import {
  DataTable,
  Column,
  MultiSelect,
  Popover,
  SelectButton,
  ToggleButton,
  Tag,
  AutoComplete,
  Dialog,
  Select,
  InputText,
  Textarea,
  Checkbox,
  DatePicker,
  Drawer,
  Paginator,
  TreeTable,
  MenuPt,
  TabPanels,
  TabPanel,
  TabList,
  Tab,
  MeterGroup,
  ToggleSwitch,
  Divider,
  Toast,
  DualToggleSwitch,
} from "./utils/globalPassThrough";

const pinia = createPinia();
const app = createApp(App);
app.use(store);
app.use(router);
app.use(pinia);
app.use(i18n);
app.use(VueApexCharts);
app.use(VueCal);
app.use(PrimeVue, {
  theme: {
    preset: ForecastifyPreset,
    options: {
      darkModeSelector: ".dark",
    },
  },
  locale: {
    ...defaultOptions.locale,
    firstDayOfWeek: 1, // Monday
    dateFormat: "yy.mm.dd",
  },
  pt: {
    DataTable,
    Column,
    MultiSelect,
    Popover,
    SelectButton,
    ToggleButton,
    Tag,
    AutoComplete,
    Dialog,
    Select,
    InputText,
    Textarea,
    Checkbox,
    DatePicker,
    Drawer,
    Paginator,
    TreeTable,
    Menu: MenuPt,
    TabPanels,
    TabPanel,
    TabList,
    Tab,
    MeterGroup,
    ToggleSwitch,
    Divider,
    Toast,
  },
});
app.use(ToastService);
app.use(ConfirmationService);

app.use(FChartPlugin);
app.use(VueTelInput, {
  mode: "international",
});
app.component("VueTelInput", VueTelInput);
app.mount("#app");

app.config.globalProperties.gapi = window.gapi;

app.use(Buefy, {
  defaultToastPosition: "is-bottom",
  defaultSnackbarPosition: "is-bottom",
  // defaultIconComponent: "vue-fontawesome",
  defaultIconPack: "fa-regular",
  customIconPacks: {
    "fa-regular": {
      sizes: {
        "is-medium": "fa-2x",
        "is-large": "fa-3x",
      },
      iconPrefix: "fa-",
    },
  },
});
// app.use(VueApexCharts);
// app.use(vueSmoothScroll);
// Nem működik a paraméter átadás ha app szintű pluginként van behúzva a Vuelidate, mixinnel kell megoldani ahol kell (lásd többi)
// app.use(VueMask);
app.component("apexChart", VueApexCharts);
app.directive("cleave", cleave);
app.directive("styleclass", StyleClass);
app.directive("tooltip", Tooltip);

// app.config.globalProperties.$filters = {
//   percent(data) {
//     return percentify(data);
//   },
// };

// app.config.globalProperties.$filters = {
//   secondsToHoursAndMinutes(data) {
//     return secondsToHoursAndMinutes(data);
//   },
// };

// app.config.globalProperties.$filters = {
//   minutesToHoursAndMinutes(time) {
//     if (!time) {
//       return "";
//     }

//     let hours = Math.floor(time / 60);
//     let minutes = time % 60;
//     let formatted = `${hours} óra`;
//     if (minutes !== 0) {
//       formatted = formatted.concat(` ${minutes} perc`);
//     }
//     return formatted;
//   },
// };

app.config.globalProperties.$filterSecondToTimeFormat = (data) =>
  secondToTimeFormat(data);

app.config.globalProperties.$filterDateFormat = (date) =>
  date ? dayjs(date).format("YYYY. MM. DD") : "-";

// app.config.globalProperties.$filters = {
//   empty(data) {
//     return data ? data : "-";
//   },
// };

// Vue.filter("secondToWorkDayFormat", function (data) {
//   return secondToWorkDayFormat(data);
// });

app.config.globalProperties.$filterHourToShortWorkDayFormat = (data) =>
  hourToShortWorkDayFormat(data);

// Vue.filter("secondToShortWorkDayFormat", function (data) {
//   return secondToShortWorkDayFormat(data);
// });

app.config.globalProperties.$filterSecondToShortWorkDayFormat = (data) =>
  secondToShortWorkDayFormat(data);

// Vue.filter("empty", function (data) {
//   return data ? data : "-";
// });

// app.config.globalProperties.$filters = {
//   quarterStatus(status) {
//     return status ? "Lezárt" : "Aktív";
//   },
// };

app.config.globalProperties.$filterPhone = (phone) =>
  phone
    ? phone
        .replace(/\D/g, "")
        .replace(/(\d{2})(\d{2})(\d{3})(\d{4})/, "+$1 $2 $3 $4")
    : "-";

// app.config.globalProperties.$filters = {
//   boolean(boolean) {
//     return boolean ? "Igen" : "Nem";
//   },
// };

// app.config.globalProperties.$filters = {
//   shortFormMoney(value, decimals = 0) {
//     let divided = value;
//     let suffix = "";
//     if (Math.abs(value) > 1000000) {
//       divided = value / 1000000;
//       suffix = "m";
//     } else if (Math.abs(value) > 1000) {
//       divided = value / 1000;
//       suffix = "e";
//     }
//     return roundToNDecimals(divided, decimals) + suffix;
//   },
// };

app.config.globalProperties.$tf = $tf;
app.config.globalProperties.$tcf = $tcf;

setConfirm(app.config.globalProperties.$confirm);

export default app;
